import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';

import {
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Database,
  Shield,
  Link,
  Users,
  FileText,
  Calendar,
  Settings,
  Trash2,
  AlertCircle,
  Info,
  TrendingUp,
  BarChart3,
  Eye,
  Download,
  Filter,
  Bug,
  PlusCircle, // Yeni ikon
  Bell, // Yeni ikon
  Edit, // Yeni ikon
  Clock, // Yeni ikon
  Check, // Yeni ikon
  X, // Yeni ikon
  Send, // Yeni ikon
  BellOff, // Yeni ikon
  BellRing // Yeni ikon
} from 'lucide-react';

// Supabase client
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const DataIntegrityCheck = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState({});
  const [selectedCheck, setSelectedCheck] = useState('all');
  const [userRole, setUserRole] = useState(null);
  const [showDetails, setShowDetails] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, critical, warning, success

  // Sistem Bildirimleri için yeni state'ler
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationContent, setNotificationContent] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [editingNotificationId, setEditingNotificationId] = useState(null);

  // TipTap editor setup
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: notificationContent,
    onUpdate: ({ editor }) => {
      setNotificationContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4',
      },
    },
  });

  // Check definitions
  const integrityChecks = [
    {
      id: 'orphaned_tasks',
      name: 'Yetim Görevler',
      description: 'Silinmiş projeler veya kullanıcılara bağlı görevleri kontrol eder',
      icon: FileText,
      category: 'tasks',
      severity: 'critical'
    },
    {
      id: 'missing_user_profiles',
      name: 'Eksik Kullanıcı Profilleri',
      description: 'Auth tablosunda olan ama user_profiles tablosunda olmayan kullanıcıları bulur',
      icon: Users,
      category: 'users',
      severity: 'critical'
    },
    {
      id: 'broken_file_references',
      name: 'Kırık Dosya Referansları',
      description: 'Storage\'da olmayan ama veritabanında referansı bulunan dosyaları tespit eder',
      icon: Link,
      category: 'files',
      severity: 'warning'
    },
    {
      id: 'invalid_task_dates',
      name: 'Geçersiz Görev Tarihleri',
      description: 'Bitiş tarihi başlangıç tarihinden önce olan görevleri bulur',
      icon: Calendar,
      category: 'tasks',
      severity: 'warning'
    },
    {
      id: 'duplicate_categories',
      name: 'Çift Kategoriler',
      description: 'Aynı isimde birden fazla kategori olup olmadığını kontrol eder',
      icon: Settings,
      category: 'categories',
      severity: 'warning'
    },
    {
      id: 'missing_project_references',
      name: 'Eksik Proje Referansları',
      description: 'Görevlerde mevcut olmayan projelere referans olup olmadığını kontrol eder',
      icon: Database,
      category: 'projects',
      severity: 'critical'
    },
    {
      id: 'inactive_user_data',
      name: 'İnaktif Kullanıcı Verileri',
      description: 'Uzun süredir giriş yapmamış kullanıcıların aktif verilerini kontrol eder',
      icon: Shield,
      category: 'users',
      severity: 'info'
    },
    {
      id: 'large_unused_files',
      name: 'Büyük Kullanılmayan Dosyalar',
      description: 'Hiçbir kayıtta referansı olmayan büyük dosyaları tespit eder',
      icon: Trash2,
      category: 'files',
      severity: 'warning'
    }
  ];

  // Initialize user check
  useEffect(() => {
    checkUserPermissions();
    fetchSystemNotifications();
  }, []);

  // Update editor content when editing notification
  useEffect(() => {
    if (editor && notificationContent !== editor.getHTML()) {
      editor.commands.setContent(notificationContent);
    }
  }, [editor, notificationContent]);

  const checkUserPermissions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        setUserRole(profile?.role);
        
        if (profile?.role !== 'super') {
          toast.error('Bu özellik sadece süper yöneticiler tarafından kullanılabilir');
        }
      }
    } catch (error) {
      console.error('User permission check failed:', error);
    }
  };

  // Sistem Bildirimlerini Getir
  const fetchSystemNotifications = async () => {
    setIsNotificationLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_notifications')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching system notifications:', error);
      toast.error('Sistem bildirimleri yüklenirken hata oluştu.');
    } finally {
      setIsNotificationLoading(false);
    }
  };

  // Sistem Bildirimi Oluştur/Güncelle
  const handleSaveNotification = async (e) => {
    e.preventDefault();
    const contentText = editor?.getText() || '';
    if (!notificationTitle || !notificationContent || contentText.trim() === '') {
      toast.error('Başlık ve içerik boş olamaz.');
      return;
    }
    if (userRole !== 'super') {
      toast.error('Yetkiniz bulunmuyor');
      return;
    }

    setIsNotificationLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Kullanıcı oturumu bulunamadı.');
        setIsNotificationLoading(false);
        return;
      }

      if (editingNotificationId) {
        // Güncelleme
        const { error } = await supabase
          .from('system_notifications')
          .update({
            title: notificationTitle,
            content: notificationContent,
          })
          .eq('id', editingNotificationId);

        if (error) throw error;
        toast.success('Bildirim başarıyla güncellendi!');
      } else {
        // Oluşturma
        const { error } = await supabase
          .from('system_notifications')
          .insert({
            title: notificationTitle,
            content: notificationContent,
            created_by: user.id,
            is_active: true, // Varsayılan olarak aktif
          });

        if (error) throw error;
        toast.success('Bildirim başarıyla oluşturuldu!');
      }

      setNotificationTitle('');
      setNotificationContent('');
      setEditingNotificationId(null);
      fetchSystemNotifications(); // Listeyi güncelle
    } catch (error) {
      console.error('Error saving notification:', error);
      toast.error('Bildirim kaydedilirken hata oluştu.');
    } finally {
      setIsNotificationLoading(false);
    }
  };

  // Bildirimi düzenleme moduna al
  const handleEditNotification = (notification) => {
    setEditingNotificationId(notification.id);
    setNotificationTitle(notification.title);
    setNotificationContent(notification.content);
  };

  // Bildirimi iptal et
  const handleCancelEdit = () => {
    setEditingNotificationId(null);
    setNotificationTitle('');
    setNotificationContent('');
  };

  // Bildirimi sil
  const handleDeleteNotification = async (id) => {
    if (!window.confirm('Bu bildirimi silmek istediğinizden emin misiniz?')) return;
    if (userRole !== 'super') {
      toast.error('Yetkiniz bulunmuyor');
      return;
    }

    setIsNotificationLoading(true);
    try {
      const { error } = await supabase
        .from('system_notifications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Bildirim başarıyla silindi!');
      fetchSystemNotifications(); // Listeyi güncelle
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Bildirim silinirken hata oluştu.');
    } finally {
      setIsNotificationLoading(false);
    }
  };

  // Bildirimin aktiflik durumunu değiştir
  const handleToggleNotificationActive = async (id, currentStatus) => {
    if (userRole !== 'super') {
      toast.error('Yetkiniz bulunmuyor');
      return;
    }

    setIsNotificationLoading(true);
    try {
      const { error } = await supabase
        .from('system_notifications')
        .update({ is_active: !currentStatus })
        .eq('id', id);

      if (error) throw error;
      toast.success(`Bildirim başarıyla ${!currentStatus ? 'aktif' : 'pasif'} edildi!`);
      fetchSystemNotifications(); // Listeyi güncelle
    } catch (error) {
      console.error('Error toggling notification status:', error);
      toast.error('Bildirim durumu güncellenirken hata oluştu.');
    } finally {
      setIsNotificationLoading(false);
    }
  };

  // Main integrity check function
  const runIntegrityCheck = async (checkId = 'all') => {
    if (userRole !== 'super') {
      toast.error('Yetkiniz bulunmuyor');
      return;
    }

    setIsLoading(true);
    const newResults = {};

    try {
      const checksToRun = checkId === 'all' 
        ? integrityChecks 
        : integrityChecks.filter(check => check.id === checkId);

      for (const check of checksToRun) {
        try {
          const result = await executeCheck(check.id);
          newResults[check.id] = {
            ...result,
            checkInfo: check,
            timestamp: new Date().toISOString()
          };
        } catch (error) {
          newResults[check.id] = {
            status: 'error',
            message: `Kontrol sırasında hata oluştu: ${error.message}`,
            issues: [],
            checkInfo: check,
            timestamp: new Date().toISOString()
          };
        }
      }

      setResults(prev => ({ ...prev, ...newResults }));
      
      const totalIssues = Object.values(newResults).reduce((sum, result) => 
        sum + (result.issues?.length || 0), 0
      );
      
      if (totalIssues > 0) {
        toast.warning(`${totalIssues} sorun tespit edildi`);
      } else {
        toast.success('Tüm kontroller başarılı!');
      }

    } catch (error) {
      console.error('Integrity check failed:', error);
      toast.error('Veri bütünlüğü kontrolü başarısız oldu');
    } finally {
      setIsLoading(false);
    }
  };

  // Individual check functions
  const executeCheck = async (checkId) => {
    switch (checkId) {
      case 'orphaned_tasks':
        return await checkOrphanedTasks();
      case 'missing_user_profiles':
        return await checkMissingUserProfiles();
      case 'broken_file_references':
        return await checkBrokenFileReferences();
      case 'invalid_task_dates':
        return await checkInvalidTaskDates();
      case 'duplicate_categories':
        return await checkDuplicateCategories();
      case 'missing_project_references':
        return await checkMissingProjectReferences();
      case 'inactive_user_data':
        return await checkInactiveUserData();
      case 'large_unused_files':
        return await checkLargeUnusedFiles();
      default:
        throw new Error('Bilinmeyen kontrol türü');
    }
  };

  // Check implementations
  const checkOrphanedTasks = async () => {
    const { data: orphanedTasks, error } = await supabase
      .from('tasks')
      .select(`
        id, title, project_id, created_by,
        project:projects(id),
        creator:user_profiles!created_by(id)
      `);

    if (error) throw error;

    const issues = orphanedTasks.filter(task => 
      (task.project_id && !task.project) || !task.creator
    );

    return {
      status: issues.length > 0 ? 'warning' : 'success',
      message: issues.length > 0 
        ? `${issues.length} yetim görev bulundu`
        : 'Yetim görev bulunamadı',
      issues: issues.map(task => ({
        id: task.id,
        description: `Görev: ${task.title}`,
        details: task.project_id && !task.project 
          ? 'Silinmiş projeye bağlı' 
          : 'Silinmiş kullanıcıya bağlı',
        severity: 'critical'
      }))
    };
  };

  const checkMissingUserProfiles = async () => {
    // Bu kontrol için admin yetkisi gerekir, basit bir örnek
    const { data: profiles, error } = await supabase
      .from('user_profiles')
      .select('id, email');

    if (error) throw error;

    // Basit kontrol - gerçek uygulamada auth.users tablosu ile karşılaştırılmalı
    const issues = profiles.filter(profile => !profile.email);

    return {
      status: issues.length > 0 ? 'critical' : 'success',
      message: issues.length > 0 
        ? `${issues.length} eksik profil bulundu`
        : 'Tüm kullanıcı profilleri mevcut',
      issues: issues.map(profile => ({
        id: profile.id,
        description: `Kullanıcı ID: ${profile.id}`,
        details: 'Email bilgisi eksik',
        severity: 'critical'
      }))
    };
  };

  const checkBrokenFileReferences = async () => {
    const { data: tasksWithFiles, error } = await supabase
      .from('tasks')
      .select('id, title, attachments')
      .not('attachments', 'is', null);

    if (error) throw error;

    const issues = [];
    
    for (const task of tasksWithFiles) {
      if (task.attachments && Array.isArray(task.attachments)) {
        for (const file of task.attachments) {
          if (file.url) {
            // Basit URL kontrolü - gerçek uygulamada storage'dan kontrol edilmeli
            if (!file.url.includes('supabase.co')) {
              issues.push({
                id: `${task.id}-${file.name}`,
                description: `Görev: ${task.title}`,
                details: `Kırık dosya linki: ${file.name}`,
                severity: 'warning'
              });
            }
          }
        }
      }
    }

    return {
      status: issues.length > 0 ? 'warning' : 'success',
      message: issues.length > 0 
        ? `${issues.length} kırık dosya referansı bulundu`
        : 'Tüm dosya referansları geçerli',
      issues
    };
  };

  const checkInvalidTaskDates = async () => {
    const { data: tasks, error } = await supabase
      .from('tasks')
      .select('id, title, start_date, end_date')
      .not('start_date', 'is', null)
      .not('end_date', 'is', null);

    if (error) throw error;

    const issues = tasks.filter(task => 
      new Date(task.end_date) < new Date(task.start_date)
    );

    return {
      status: issues.length > 0 ? 'warning' : 'success',
      message: issues.length > 0 
        ? `${issues.length} geçersiz tarih bulundu`
        : 'Tüm görev tarihleri geçerli',
      issues: issues.map(task => ({
        id: task.id,
        description: `Görev: ${task.title}`,
        details: `Bitiş tarihi (${new Date(task.end_date).toLocaleDateString()}) başlangıç tarihinden (${new Date(task.start_date).toLocaleDateString()}) önce`,
        severity: 'warning'
      }))
    };
  };

  const checkDuplicateCategories = async () => {
    const { data: categories, error } = await supabase
      .from('categories')
      .select('id, name');

    if (error) throw error;

    const nameGroups = {};
    categories.forEach(cat => {
      const name = cat.name.toLowerCase().trim();
      if (!nameGroups[name]) nameGroups[name] = [];
      nameGroups[name].push(cat);
    });

    const duplicates = Object.entries(nameGroups)
      .filter(([name, cats]) => cats.length > 1);

    const issues = duplicates.flatMap(([name, cats]) => 
      cats.slice(1).map(cat => ({
        id: cat.id,
        description: `Kategori: ${cat.name}`,
        details: `"${name}" ismiyle ${cats.length} kategori mevcut`,
        severity: 'warning'
      }))
    );

    return {
      status: issues.length > 0 ? 'warning' : 'success',
      message: issues.length > 0 
        ? `${duplicates.length} çift kategori grubu bulundu`
        : 'Çift kategori bulunamadı',
      issues
    };
  };

  const checkMissingProjectReferences = async () => {
    const { data: tasks, error } = await supabase
      .from('tasks')
      .select(`
        id, title, project_id,
        project:projects(id)
      `)
      .not('project_id', 'is', null);

    if (error) throw error;

    const issues = tasks.filter(task => task.project_id && !task.project);

    return {
      status: issues.length > 0 ? 'critical' : 'success',
      message: issues.length > 0 
        ? `${issues.length} eksik proje referansı bulundu`
        : 'Tüm proje referansları geçerli',
      issues: issues.map(task => ({
        id: task.id,
        description: `Görev: ${task.title}`,
        details: `Mevcut olmayan proje ID: ${task.project_id}`,
        severity: 'critical'
      }))
    };
  };

  const checkInactiveUserData = async () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: users, error } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, last_seen')
      .lt('last_seen', thirtyDaysAgo.toISOString());

    if (error) throw error;

    // Bu kullanıcıların aktif verilerini kontrol et
    const activeDataChecks = await Promise.all(
      users.map(async (user) => {
        const { count: taskCount } = await supabase
          .from('tasks')
          .select('id', { count: 'exact' })
          .eq('created_by', user.id)
          .gte('created_at', thirtyDaysAgo.toISOString());

        return { user, taskCount };
      })
    );

    const issues = activeDataChecks
      .filter(({ taskCount }) => taskCount > 0)
      .map(({ user, taskCount }) => ({
        id: user.id,
        description: `Kullanıcı: ${user.first_name} ${user.last_name}`,
        details: `30 gün giriş yapmamış ama ${taskCount} yeni görev oluşturmuş`,
        severity: 'info'
      }));

    return {
      status: issues.length > 0 ? 'info' : 'success',
      message: issues.length > 0 
        ? `${issues.length} inaktif kullanıcıda aktif veri bulundu`
        : 'İnaktif kullanıcılarda aktif veri yok',
      issues
    };
  };

  const checkLargeUnusedFiles = async () => {
    // Bu örnek basitleştirilmiş - gerçek uygulamada storage API kullanılmalı
    const { data: tasks, error } = await supabase
      .from('tasks')
      .select('attachments');

    if (error) throw error;

    const usedFiles = new Set();
    tasks.forEach(task => {
      if (task.attachments && Array.isArray(task.attachments)) {
        task.attachments.forEach(file => {
          if (file.url) usedFiles.add(file.url);
        });
      }
    });

    // Simulated large unused files
    const issues = [];
    
    return {
      status: issues.length > 0 ? 'warning' : 'success',
      message: issues.length > 0 
        ? `${issues.length} büyük kullanılmayan dosya bulundu`
        : 'Büyük kullanılmayan dosya bulunamadı',
      issues
    };
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-green-600 bg-green-50 border-green-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'critical':
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
      default:
        return <CheckCircle className="w-5 h-5" />;
    }
  };

  // Filter results
  const filteredResults = Object.entries(results).filter(([id, result]) => {
    if (searchTerm && !result.checkInfo.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    if (filterStatus !== 'all') {
      if (filterStatus === 'critical' && result.status !== 'critical') return false;
      if (filterStatus === 'warning' && result.status !== 'warning') return false;
      if (filterStatus === 'success' && result.status !== 'success') return false;
    }
    
    return true;
  });

  if (userRole !== 'super') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Yetkisiz Erişim
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Bu özellik sadece süper yöneticiler tarafından kullanılabilir.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <Database className="w-8 h-8 text-blue-600" />
                Veri Bütünlüğü Kontrolü
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Sistemdeki veri tutarlılığını ve kalitesini kontrol edin
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => runIntegrityCheck()}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Kontrol Ediliyor...' : 'Tüm Kontroller'}
              </button>
            </div>
          </div>
        </div>

        {/* Checks Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {integrityChecks.map((check) => {
            const result = results[check.id];
            const Icon = check.icon;
            
            return (
              <motion.div
                key={check.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`flex items-center gap-3`}>
                      <div className={`p-2 rounded-lg ${getSeverityColor(check.severity)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {check.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {check.description}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => runIntegrityCheck(check.id)}
                      disabled={isLoading}
                      className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {result && (
                    <div className="space-y-3">
                      {/* Status */}
                      <div className={`flex items-center gap-2 p-3 rounded-lg border ${getSeverityColor(result.status)}`}>
                        {getStatusIcon(result.status)}
                        <span className="font-medium">{result.message}</span>
                      </div>

                      {/* Issues */}
                      {result.issues && result.issues.length > 0 && (
                        <div>
                          <button
                            onClick={() => setShowDetails(prev => ({
                              ...prev,
                              [check.id]: !prev[check.id]
                            }))}
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                          >
                            <Eye className="w-4 h-4" />
                            {showDetails[check.id] ? 'Detayları Gizle' : 'Detayları Göster'}
                            ({result.issues.length})
                          </button>

                          <AnimatePresence>
                            {showDetails[check.id] && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-3 space-y-2 max-h-64 overflow-y-auto"
                              >
                                {result.issues.map((issue, index) => (
                                  <div
                                    key={index}
                                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {issue.description}
                                    </div>
                                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                      {issue.details}
                                    </div>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}

                      {/* Timestamp */}
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Son kontrol: {new Date(result.timestamp).toLocaleString('tr-TR')}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Summary Stats */}
        {Object.keys(results).length > 0 && (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Kontrol Özeti
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['critical', 'warning', 'success', 'error'].map(status => {
                const count = Object.values(results).filter(r => r.status === status).length;
                const total = Object.keys(results).length;
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                
                return (
                  <div key={status} className="text-center">
                    <div className={`text-2xl font-bold ${
                      status === 'critical' ? 'text-red-600' :
                      status === 'warning' ? 'text-yellow-600' :
                      status === 'error' ? 'text-red-500' :
                      'text-green-600'
                    }`}>
                      {count}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {status === 'critical' ? 'Kritik' :
                       status === 'warning' ? 'Uyarı' :
                       status === 'error' ? 'Hata' :
                       'Başarılı'}
                    </div>
                    <div className="text-xs text-gray-500">
                      %{percentage}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Sistem Bildirim Yönetimi */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mt-8"> {/* mt-8 ekleyerek üstten boşluk bıraktık */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Bell className="w-6 h-6 text-primary-600" />
              Sistem Bildirim Yönetimi
            </h2>
            <button
              onClick={() => {
                setEditingNotificationId(null);
                setNotificationTitle('');
                setNotificationContent('');
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
            >
              <PlusCircle className="w-5 h-5" />
              Yeni Bildirim Oluştur
            </button>
          </div>

          {/* Bildirim Oluşturma/Düzenleme Formu */}
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-8 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {editingNotificationId ? 'Bildirimi Düzenle' : 'Yeni Bildirim Oluştur'}
            </h3>
            <form onSubmit={handleSaveNotification}>
              <div className="mb-4">
                <label htmlFor="notificationTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Başlık
                </label>
                <input
                  type="text"
                  id="notificationTitle"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  placeholder="Bildirim Başlığı"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="notificationContent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  İçerik
                </label>
                <div className="rounded-2xl border border-gray-300 dark:border-gray-200 overflow-hidden bg-white dark:bg-gray-800">
                  {/* Toolbar */}
                  <div className="border-b border-gray-200 dark:border-gray-600 p-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('bold') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      <strong>B</strong>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('italic') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      <em>I</em>
                    </button>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleStrike().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('strike') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      <s>S</s>
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('heading', { level: 1 }) ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      H1
                    </button>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      H2
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleBulletList().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('bulletList') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      •
                    </button>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('orderedList') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      1.
                    </button>
                    <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                      className={`p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        editor?.isActive('blockquote') ? 'bg-gray-200 dark:bg-gray-600' : ''
                      }`}
                    >
                      "
                    </button>
                    <button
                      type="button"
                      onClick={() => editor?.chain().focus().setHorizontalRule().run()}
                      className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      —
                    </button>
                  </div>
                  {/* Editor Content */}
                  <EditorContent 
                    editor={editor} 
                    className="min-h-[200px] p-4 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                {editingNotificationId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    disabled={isNotificationLoading}
                  >
                    <X className="w-5 h-5" />
                    İptal Et
                  </button>
                )}
                <button
                  type="submit"
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={isNotificationLoading}
                >
                  {isNotificationLoading ? (
                    <RefreshCw className="w-5 h-5 animate-spin" />
                  ) : editingNotificationId ? (
                    <Edit className="w-5 h-5" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                  {isNotificationLoading ? 'Kaydediliyor...' : editingNotificationId ? 'Güncelle' : 'Bildirim Oluştur'}
                </button>
              </div>
            </form>
          </motion.div>

          {/* Mevcut Bildirimler Listesi */}
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Mevcut Sistem Bildirimleri
            {isNotificationLoading && <RefreshCw className="w-4 h-4 animate-spin ml-2 text-gray-500" />}
          </h3>
          {isNotificationLoading && notifications.length === 0 ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Bildirimler yükleniyor...</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Henüz sistem bildirimi bulunmuyor.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div key={notification.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {notification.title}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(notification.created_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${notification.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {notification.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                      <button
                        onClick={() => handleToggleNotificationActive(notification.id, notification.is_active)}
                        className="p-1.5 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title={notification.is_active ? 'Pasif Yap' : 'Aktif Yap'}
                        disabled={isNotificationLoading}
                      >
                        {notification.is_active ? (
                          <BellOff className="w-4 h-4" />
                        ) : (
                          <BellRing className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEditNotification(notification)}
                        className="p-1.5 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title="Düzenle"
                        disabled={isNotificationLoading}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNotification(notification.id)}
                        className="p-1.5 rounded-full text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                        title="Sil"
                        disabled={isNotificationLoading}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="prose dark:prose-invert max-w-none text-gray-800 dark:text-gray-200" dangerouslySetInnerHTML={{ __html: notification.content }} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataIntegrityCheck; 